// Every Vulnerability Watch sentence and label lives here, so the wording test covers all of it.
import type { AttemptResult, FurthestStep, ShieldAnswer, SourcesSort, WhoBucket } from "@/types";

export type WatchStatus = "affects" | "fixed" | "check";
export type Detection = "claimed" | "probed" | "none";

export const NOT_KNOWN = "Not known";
export const TITLE = "Vulnerability Watch";
export const LOAD_FAILED = "Failed to load Vulnerability Watch.";
export const NO_VULNERABILITIES = "No exploited vulnerabilities listed for your product.";
export const KPI_PRODUCT = "Product";
export const KPI_LISTED = "Exploited vulnerabilities";
export const KPI_AFFECTS = "Affects your version";
export const KPI_CHECK = "Check your version";
export const COL_VULNERABILITY = "Vulnerability";
export const COL_LISTED = "Listed";
export const COL_STATUS = "Your version";
export const COL_DETECTION = "Detection";
export const COL_AT_PORTAL = "At your portal";
export const COL_REPORTED_VERSION = "Reported version";
export const COL_REASON = "Reason";
export const TIP_PRODUCT = "As Shield observed it from your integration's reports, in the last 7 days.";
export const TIP_LISTED = "Vulnerabilities on CISA's catalogue of known exploited vulnerabilities, in your product.";
export const TIP_NOT_VISIBLE = "Shield has no rule for this vulnerability's requests, so it cannot see attempts. Install the vendor's fix.";
export const TIP_AT_PORTAL = "Addresses and requests at your portal in the window that a rule ties to this vulnerability.";
export const TIP_AFFECTS = "Exploited vulnerabilities that affect the version your integration reports. Upgrading to a fixed release clears them.";
export const TIP_CHECK = "Exploited vulnerabilities where Shield cannot tell whether your version is affected. Hover over a status to see why.";
export const TIP_DETECTION =
  "Whether Shield can see attempts to exploit this vulnerability. Named: a Shield rule names it. Probed for: a Shield rule sees probes for it. Not visible: Shield has no rule for it.";
export const AT_PORTAL_TITLE = "At your portal";
export const KPI_YOUR_PRODUCT = "In your product";
export const KPI_COVERAGE = "Coverage";
export const NAMED_LATER = "Named later";
export const COL_REQUESTS = "Requests";
export const COL_RULE = "Rule";
export const NO_REQUESTS = "No requests";
export const YES = "Yes";
export const NO = "No";
export const TIP_COVERAGE = "Days on which Shield stored request lines from your portal. A day without lines is a gap, not a zero.";
export const TIP_NAMED_LATER = "Requests no rule named when they arrived. A nightly back-check named them after a rule was added.";
export const CREDIT =
  "Sources: CISA Known Exploited Vulnerabilities Catalog (CC0); CVE records (cve.org); NVD (nvd.nist.gov). This product uses the NVD API but is not endorsed or certified by the NVD.";
export const REQUIRED_ACTION = "CISA's required action";
export const VENDOR_ADVISORY = "Vendor advisory";
export const TIMELINE = "Timeline";
export const EMAILED = "Emailed";
export const TIMELINE_PUBLISHED = "Published";
export const TIMELINE_LISTED = "Listed by CISA";
export const TIMELINE_FIRST_SEEN = "First seen by Shield";
export const TABS_LABEL = "Vulnerability Watch views";
export const TAB_VULNERABILITIES = "Vulnerabilities";
export const TAB_SOURCES = "Sources";
export const PORTAL_LOAD_FAILED = "Failed to load activity at your portal.";
export const SOURCES_LOAD_FAILED = "Failed to load sources.";
export const FIG_ADDRESSES = "Addresses";
export const FIG_NAMED = "Named by a rule";
export const FIG_ATTEMPTS = "Sign-in attempts";
export const TRAFFIC = "How your traffic divides";
export const BY_STEP = "Addresses by furthest step";
export const BY_RESULT = "Sign-in attempts by result";
export const LOOKED_FOR = "What they looked for";
export const WHO_THEY_ARE = "Who they are";
export const SHOW_TOP = "Show top 5";
export const HIDE_TOP = "Hide top 5";
export const OPEN_IN_SOURCES = "Open in Sources";
export const TIP_ADDRESSES = "Addresses with a stored request line, a query from your portal or a sign-in outcome in the window.";
export const TIP_NAMED = "Addresses with at least one request that a Shield rule names.";
export const TIP_ATTEMPTS = "Sign-in attempts your portal reported in the window, blocked ones included.";
export const TIP_TRAFFIC = "Each address counts once, at the furthest step it reached in the window. The darker part is the share Shield blocks. Under the default retention policy, Shield stores request lines only for flagged requests. So Never reached sign-in counts suspicious addresses, not every visitor.";
export const TIP_LOOKED_FOR = "Each rule belongs to one group, and an address counts once in each group it looked for. Not grouped yet: requests named before the groups existed, until the nightly back-check judges their day again.";
export const TIP_WHO = "The kind of service behind an address with request lines, from Shield's catalogue of known services. Shield never shows who runs it.";
export const TIP_SEARCH = "The start of an address, such as 203.0.113, or a CVE id.";
export const SEARCH_LABEL = "Address or CVE";
export const FILTERS = "Filters";
export const RESET = "Reset";
export const PRESETS = "Presets";
export const ANY_TIME = "Any time";
export const FILTER_LOOKED_FOR = "Looked for";
export const FILTER_YOUR_PRODUCT = "Your product";
export const FILTER_STEP = "Furthest step";
export const FILTER_ANSWER = "Shield's answer";
export const FILTER_WHO = "Who";
export const FILTER_COUNTRY = "Country";
export const FILTER_NETWORK = "Network";
export const FILTER_FIRST_SEEN = "First seen";
export const COL_ADDRESS = "Address";
export const COL_LAST_SEEN = "Last seen";
export const SORT_LABEL = "Sort";
export const ORDER_ASC = "Ascending";
export const ORDER_DESC = "Descending";
export const NO_SOURCES = "No addresses match these filters.";
export const NOT_NAMED = "Not named";
export const COL_DETAIL = "Details";
export const showDetailLabel = (address: string) => `Show details for ${address}`;
export const hideDetailLabel = (address: string) => `Hide details for ${address}`;
export const PAGE_PREVIOUS = "Previous page";
export const PAGE_NEXT = "Next page";
export const DETAIL_BY_GROUP = "Requests by group";
export const DETAIL_RULES = "Top rules";
export const PRESET_YOUR_PRODUCT = "Tried your product";
export const PRESET_SECRET_FILES = "Looked for secret files";
export const PRESET_FAILED = "Tried and failed";
export const PRESET_NO_ATTEMPT = "Reached sign-in, no attempt";
export const PRESET_KNOWN_SCANNERS = "Known scanners";
export const PRESET_NEW = "New in the last 7 days";
export const OTHER = "Other";
export const OTHER_KNOWN_SERVICE = "Other known service";

const STATUS: Record<WatchStatus, string> = {
  affects: "Affects your version",
  fixed: "Fixed in your version",
  check: "Check your version",
};
const DETECTION: Record<Detection, string> = { claimed: "Named", probed: "Probed for", none: "Not visible" };

export function statusLabel(status: WatchStatus): string {
  return STATUS[status];
}

export function detectionLabel(detection: Detection): string {
  return DETECTION[detection];
}

export function productLabel(vendor: string | null, product: string | null, version: string | null): string {
  if (!vendor || !product) return NOT_KNOWN;
  return version ? `${vendor} ${product} ${version}` : `${vendor} ${product}, version not known`;
}

export function catalogueProductLabel(product: string, version: string | null): string {
  return version ? `${product} ${version}` : `${product}, version not known`;
}

export function reportedByLabel(
  reporters: readonly { integration: string | null; integration_version: string | null; sensor_id: string | null }[],
): string | null {
  const names = reporters
    .map((r) => {
      const name = [r.integration, r.integration_version].filter(Boolean).join(" ");
      return r.sensor_id ? [name, `(${r.sensor_id})`].filter(Boolean).join(" ") : name;
    })
    .filter(Boolean);
  return names.length > 0 ? `Reported by ${names.join(", ")}` : null;
}

export function listFetchedLabel(time: string): string {
  return `CISA list fetched ${time}`;
}

export function navBadgeLabel(count: number): string {
  return `${count} new or affecting your version`;
}

export function severityLabel(severity: string | null, cvss: number | null): string | null {
  if (!severity && cvss === null) return null;
  const level = severity ? severity.charAt(0) + severity.slice(1).toLowerCase() : "";
  return [level, cvss === null ? "" : cvss.toFixed(1)].filter(Boolean).join(" ");
}

export function atPortalLabel(
  detection: Detection,
  known: boolean,
  seen: { readonly addresses: number; readonly requests: number } | undefined,
): string {
  if (detection === "none") return DETECTION.none;
  if (!known) return NOT_KNOWN;
  if (!seen) return NO_REQUESTS;
  return `${addressesLabel(seen.addresses)} · ${requestsLabel(seen.requests)}`;
}

export function coverageLabel(daysWithLines: number, days: number): string {
  return `Request lines on ${daysWithLines.toLocaleString("en-US")} of ${days.toLocaleString("en-US")} days`;
}

export function yourProductLabel(value: boolean | null): string {
  if (value === null) return NOT_KNOWN;
  return value ? YES : NO;
}

export function countOrNotKnown(value: number | null | undefined): string {
  return value === null || value === undefined ? NOT_KNOWN : value.toLocaleString("en-US");
}

const GROUPS: Record<string, string> = {
  secretFiles: "Secret and configuration files", knownVulnerabilities: "Known vulnerabilities",
  exploits: "Other exploit attempts", scannerTools: "Scanner tools", passwordAttacks: "Password attacks",
  softwareProbes: "Probes for software and admin pages", notGrouped: "Not grouped yet",
};
const ACTOR_CLASSES: Record<string, string> = {
  researchScanner: "Research scanner", crawler: "Crawler", cdn: "CDN", monitoring: "Monitoring service",
  certValidation: "Certificate validation", webhook: "Webhook sender",
};
export const STEPS: Record<FurthestStep, string> = {
  neverReachedSignIn: "Never reached sign-in", reachedNoAttempt: "Reached sign-in, no attempt",
  refused: "Refused at sign-in", failed: "Tried and failed", signedIn: "Signed in",
};
export const ANSWERS: Record<ShieldAnswer, string> = {
  blocked: "Blocked", captcha: "CAPTCHA", allowed: "Allowed", monitored: "Monitored", notAsked: "Not asked", other: OTHER,
};
export const ATTEMPT_RESULTS: Record<AttemptResult, string> = {
  blocked: "Blocked", userNotFound: "User not found", invalidPassword: "Wrong password",
  failedSecondFactor: "Failed second factor", signedIn: "Signed in",
};
export const WHO: Record<WhoBucket, string> = {
  researchScanner: "Research scanners", crawler: "Crawlers", otherKnown: "Other known services",
  notClassified: "Not classified",
};
export const SORTS: Record<SourcesSort, string> = {
  requests: "Requests", attempts: "Sign-in attempts", lastSeen: "Last seen", firstSeen: "First seen",
};

function formatCount(value: number): string {
  return value.toLocaleString("en-US");
}

function counted(value: number, one: string, many: string): string {
  return `${formatCount(value)} ${value === 1 ? one : many}`;
}

export function groupLabel(key: string): string {
  return GROUPS[key] ?? OTHER;
}

export function stepLabel(key: string): string {
  return (STEPS as Record<string, string | undefined>)[key] ?? OTHER;
}

export function answerLabel(key: string): string {
  return (ANSWERS as Record<string, string | undefined>)[key] ?? OTHER;
}

export function actorClassLabel(value: string | null): string {
  if (value === null) return NOT_KNOWN;
  return ACTOR_CLASSES[value] ?? OTHER_KNOWN_SERVICE;
}

export function lastDaysLabel(days: number): string {
  return `Last ${days} days`;
}

export function windowCaption(days: number, updatedOn: string | null): string {
  return updatedOn === null ? lastDaysLabel(days) : `${lastDaysLabel(days)} · Updated ${updatedOn}`;
}

export function addressesLabel(addresses: number): string {
  return counted(addresses, "address", "addresses");
}

export function requestsLabel(requests: number): string {
  return counted(requests, "request", "requests");
}

export function attemptsLabel(attempts: number): string {
  return counted(attempts, "sign-in attempt", "sign-in attempts");
}

export function ruleCountLabel(rules: number): string {
  return counted(rules, "rule", "rules");
}

export function blockedLabel(addresses: number): string {
  return `${formatCount(addresses)} blocked`;
}

export function inYourProductLabel(addresses: number | null): string {
  return addresses === null ? `${KPI_YOUR_PRODUCT}: ${NOT_KNOWN}` : `${formatCount(addresses)} in your product`;
}

export function namedLaterLabel(requests: number, addresses: number): string {
  return `${requestsLabel(requests)} from ${addressesLabel(addresses)}`;
}

export function shareLabel(label: string, count: number | null): string {
  return `${label}: ${countOrNotKnown(count)}`;
}

export function openSourcesLabel(label: string): string {
  return `Open Sources: ${label}`;
}

export function ruleName(ruleId: string, label: string | null): string {
  return label ?? ruleId.slice(ruleId.indexOf("/") + 1);
}

export function itemLabel(item: { readonly kind: "cve" | "rule"; readonly key: string; readonly label: string | null }): string {
  return item.kind === "cve" ? item.key : ruleName(item.key, item.label);
}

export function showingLabel(from: number, to: number, total: number | null): string {
  if (total === null) return NOT_KNOWN;
  return `Showing ${formatCount(from)}–${formatCount(to)} of ${addressesLabel(total)}`;
}

export function firstSeenLabel(days: number, windowDays: number | null): string {
  return windowDays !== null && days >= windowDays ? "Within the window" : `Within ${days} days`;
}

export function chipLabel(name: string, value: string): string {
  return `${name}: ${value}`;
}
